import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ActivatedRoute, Route} from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';

import {UtilsService} from 'src/app/shared/services/utils.service';
import {ModalService} from 'src/app/shared/services/modal.service';
import {AuthenticationService} from '../../../services/authentication.service';
import {User} from '../../../classes/app.models';
import { Router } from '@angular/router';
import { StoreService } from 'src/app/core/store/store.service';
import { DataService } from 'src/app/shared/services/data.service';
import { ApiService } from 'src/app/shared/services/api.service';
import { ExclusiveService } from 'src/app/shared/services/exclusive.service';
import { catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProfileCompletionService } from 'src/app/shared/services/profile-completion.service';
import { CreditsRefreshService } from 'src/app/shared/services/credits-refresh.service';

@Component({
    selector: 'indiapicture-header',
    templateUrl: './header.component.html',
    styleUrls: ['./header.component.scss']
})

export class HeaderComponent implements OnInit, OnDestroy {

    @Input() containerClass = 'container-fluid light';
    @Input() headerType;

    /**
     * The AI Studio's own header. `/ai` is a sibling of the site layout and
     * renders this component itself, so it sets this and nothing else does: the
     * studio trades the labelled utility icons and the nav's Pricing item for a
     * compact Pricing menu, a bare cart and an avatar disc.
     */
    @Input() aiStudio = false;

    /**
     * Raised instead of navigating when the studio's Pricing control is used, so
     * the studio can answer with its own dialog. Off `/ai` nothing listens and
     * the control stays an ordinary link to /subscription.
     */
    @Output() pricingClick = new EventEmitter<void>();

    wishCount = 0;
    headerTypeStyle: any = '';
    customBackgroundColor: string = '#000';
    currentUser: any;
    isMobile = false;

    /* ---------- Balance pill: credits left on the account ---------- */

    /**
     * The active plans behind the pill, straight from accountDashboardData.
     *
     * Everything here is already filtered server-side: not expired (validity still
     * ahead), not deleted (neither the plan nor its order) and paid
     * (`payment_status = 1`), so a row being present means it is live — the pill
     * does no expiry arithmetic of its own.
     */
    creditPackages: any[] = [];

    /*
     * Credits and images, kept apart — a plan is credits only, images only or
     * both, and the two allowances are different units that do not add up.
     * From the response's `balance` (live plans only: not expired, not deleted);
     * see applyBalance().
     */
    /** Credits left across credit and credits + images plans. */
    creditsRemaining = 0;
    /** Credits granted, so the card can read "180 of 200 left" rather than a bare count. */
    creditsTotal = 0;
    /** Plans that carry credits; 0 hides the credits figure. */
    creditPlans = 0;

    /** Images left across image and credits + images plans. */
    imagesRemaining = 0;
    imagesTotal = 0;
    imagePlans = 0;

    imageDownloaded = 0;
    videoDownloaded = 0;
    aiDownloaded = 0;

    /**
     * Until the first response lands the pill is not drawn at all. A pill reading
     * "0 credits" while the request is still out is worse than no pill: it says the
     * account is empty when nothing is known yet.
     */
    creditsLoaded = false;

    /**
     * Which account the numbers belong to. getCurrentUser() re-emits on every
     * navigation, and without this the header would re-fetch the dashboard on each
     * one; it only re-asks when the account itself changes.
     */
    private creditsForCustomer: string | null = null;

    private destroy$ = new Subject<void>();


    constructor(public activeRoute: ActivatedRoute, 
                public utilsService: UtilsService, 
                public modalService: ModalService,
                private authenticationService: AuthenticationService, 
                private authService: AuthenticationService, 
                public router: Router,
                public storeService: StoreService,
                public dataService: DataService,
                private apiService: ApiService,
                private exclusiveService: ExclusiveService,
                private storageService: StoreService,
                private profileCompletion: ProfileCompletionService,
                private creditsRefresh: CreditsRefreshService) {
                    const user = storeService.getItem('currentUser');
                    if(user && user?.isExclusive && user?.isExclusive == false){
                        this.authenticationService.currentUser.subscribe(x => this.currentUser = x);
                    }else{
                        this.currentUser = user;
                    }
            }

    ngOnInit(): void {
        if (this.headerType != 'top') {
            this.headerTypeStyle = '';
            this.customBackgroundColor = '#dfeef7';
            this.containerClass = 'container-fluid dark';
        }
        if (window.innerWidth < 600) {
            this.isMobile = true;
        }
        this.getCurrentUser();
        this.refreshCredits();
    }

    getCurrentUser() {
        this.dataService.currentData$
            .pipe(takeUntil(this.destroy$))
            .subscribe((data:any) => {
         //  console.log('index component', data);
            if (data) {
            this.currentUser = data;
            }else{
                const user = this.storageService.getItem('currentUser');
             //  console.log('StorageService ==>', user);
                if (user) {
                 //  console.log('currentUser ==> ', user);
                    this.currentUser = user;
                }else{
                     this.currentUser = null;
                }
            }

            // Signing in or out lands here, so the balance follows the account
            // without the header needing its own event.
            this.refreshCredits();
        });

        /*
         * Raised by AppInterceptor after any successful POST to our backends —
         * a generation, an edit, a stock download. Forced, because the customer
         * has not changed and the guard in refreshCredits() exists to skip
         * exactly that case.
         *
         * Debounced because one action can be several POSTs: a studio turn that
         * uploads a reference and then generates would otherwise read the
         * balance twice, and the first read would land before the spend.
         */
        this.creditsRefresh.changes$
            .pipe(debounceTime(600), takeUntil(this.destroy$))
            .subscribe(() => this.refreshCredits(true));
    }

    /**
     * Suppresses the account dropdown after a menu item has been used.
     *
     * The dropdown opens on :hover alone, so navigating from it leaves the
     * pointer still over the wrapper and the menu still open on the page it just
     * went to. There is no "open" state to clear — this is the flag that holds
     * it shut until the pointer leaves, which onAccountMenuLeave() resets.
     */
    profileMenuDismissed = false;

    /** Lets the dropdown open again once the pointer has moved off it. */
    onAccountMenuLeave(): void {
        this.profileMenuDismissed = false;
    }

    closeProfileMenu(): void {
        this.profileMenuDismissed = true;
    }

    /**
     * Navigate from an account-menu item.
     *
     * Router.navigate() rather than routerLink so the menu can be closed on the
     * way out. It also resolves the path from the root, which routerLink does
     * not: "exclusive/account/dashboard" (no leading slash) was RELATIVE to the
     * current URL, so from /products/123 it aimed at
     * /products/123/exclusive/account/dashboard.
     */
    goToAccount(path: string): void {
        this.closeProfileMenu();
        this.router.navigate([path]);
    }

    /**
     * Whether to mark the account icon with a "finish your profile" dot.
     *
     * The same rule the dashboard banner uses — one service, so the badge and
     * the page it leads to cannot disagree about whether anything is missing.
     */
    get profileIncomplete(): boolean {
        return this.profileCompletion.isIncomplete(this.currentUser);
    }

    /* ---------- Balance pill ---------- */

    /**
     * Whether the pill has something to say: signed in, and the dashboard has
     * answered.
     *
     * Exclusive accounts used to be excluded here, because /accountDashboardData
     * was the non-exclusive endpoint and knows nothing about the exclusive plan
     * and order tables. They now have a twin of it in the same response shape, so
     * the surface no longer decides whether the pill exists — only which address
     * it is fetched from. See refreshCredits().
     */
    get showCredits(): boolean {
        // console.log('showCredits', this.creditsLoaded , this.currentUser?.customers_id);
        return this.creditsLoaded && !!this.currentUser?.customers_id;
    }

    /**
     * Fetch the balance, but only when the account has actually changed.
     *
     * Signing out clears it rather than leaving the last account's number in the
     * header, which is the one state a stale read would be visible in.
     */
    private refreshCredits(force = false): void {
        const customerId = this.currentUser?.customers_id || null;

        if (!customerId) {
            this.resetCredits();
            return;
        }

        /*
         * The account-changed guard, skipped when something has told us the
         * balance moved. Without `force` a refresh after a spend does nothing at
         * all: the customer has not changed, which is exactly the case this is
         * normally here to skip.
         */
        if (!force && customerId === this.creditsForCustomer) { return; }
        this.creditsForCustomer = customerId;

        /*
         * Two endpoints, one shape. An exclusive account's plans and downloads
         * live in their own tables, so the non-exclusive endpoint cannot answer
         * for them — it is not that the number is different, it is that it is not
         * there at all.
         */
        /*
         * Both fields, matching ExclusiveService.isExclusiveSession — they are
         * set on different sign-in paths, and the two must agree on what
         * "exclusive" means or an account lands on the endpoint that cannot
         * answer for it.
         */
        const isExclusive = (this.currentUser as any)?.isExclusive === true
            || (this.currentUser as any)?.user_type === 'EXCLUSIVE';

        const dashboard$ = isExclusive
            ? this.exclusiveService.getAccountDashboardData()
            : this.apiService.getAccountDashboardData();

        dashboard$
            .pipe(
                // The header must never be the thing that breaks a page: a failed
                // balance simply leaves the pill undrawn.
                catchError(() => of(null)),
                takeUntil(this.destroy$)
            )
            .subscribe((res: any) => {
                if (!res) {
                    this.resetCredits();
                    // Cleared so the next navigation retries rather than caching a failure.
                    this.creditsForCustomer = null;
                    return;
                }

                this.creditPackages = res.customerPackage || [];
                this.applyBalance(res.balance);
                this.imageDownloaded = Number(res.totalImageDownloaded) || 0;
                this.videoDownloaded = Number(res.totalVideoDownloaded) || 0;
                this.aiDownloaded = Number(res.totalAIDownloaded) || 0;
                this.creditsLoaded = true;
            });
    }

    /**
     * The pill's two figures. From the server's `balance` when it sends one;
     * otherwise worked out here from the plans by entitlement, the same rule
     * (credits: credits / both / none · images: images / both).
     */
    private applyBalance(balance: any): void {
        if (balance?.credits && balance?.images) {
            this.creditsRemaining = Number(balance.credits.remaining) || 0;
            this.creditsTotal = Number(balance.credits.total) || 0;
            this.creditPlans = Number(balance.credits.plans) || 0;
            this.imagesRemaining = Number(balance.images.remaining) || 0;
            this.imagesTotal = Number(balance.images.total) || 0;
            this.imagePlans = Number(balance.images.plans) || 0;
            return;
        }

        const creditRows = this.creditPackages.filter(p => this.hasCredits(p));
        const imageRows = this.creditPackages.filter(p => this.hasImages(p));
        this.creditsRemaining = creditRows.reduce((t, p) => t + this.packageCreditsLeft(p), 0);
        this.creditsTotal = creditRows.reduce((t, p) => t + this.packageCreditsTotal(p), 0);
        this.creditPlans = creditRows.length;
        this.imagesRemaining = this.sumBy(imageRows, 'images_remaining');
        this.imagesTotal = this.sumBy(imageRows, 'image_allowance');
        this.imagePlans = imageRows.length;
    }

    /** A plan's credit allowance applies: credits or credits + images (older plans have no entitlement). */
    hasCredits(pkg: any): boolean {
        return !pkg?.entitlement || pkg.entitlement === 'credits' || pkg.entitlement === 'both';
    }

    /** A plan's image allowance applies: images or credits + images. */
    hasImages(pkg: any): boolean {
        return pkg?.entitlement === 'images' || pkg?.entitlement === 'both';
    }

    /** The credits figure is drawn when a plan carries credits, or when there is no plan at all (as "0 credits"). */
    get showCreditFigure(): boolean {
        return this.creditPlans > 0 || this.imagePlans === 0;
    }

    get showImageFigure(): boolean {
        return this.imagePlans > 0;
    }

    /** "700 credits · 312 images", for the pill's label and tooltip. */
    get balanceLabel(): string {
        const parts: string[] = [];
        if (this.showCreditFigure) { parts.push(this.creditsRemaining + ' credits'); }
        if (this.showImageFigure) { parts.push(this.imagesRemaining + ' images'); }
        return parts.join(' · ') + ' remaining';
    }

    private resetCredits(): void {
        this.creditPackages = [];
        this.creditsRemaining = 0;
        this.creditsTotal = 0;
        this.creditPlans = 0;
        this.imagesRemaining = 0;
        this.imagesTotal = 0;
        this.imagePlans = 0;
        this.imageDownloaded = 0;
        this.videoDownloaded = 0;
        this.aiDownloaded = 0;
        this.creditsLoaded = false;
        this.creditsForCustomer = null;
    }

    /** Totals across plans — an account can hold more than one at a time. */
    private sumBy(rows: any[], key: string): number {
        return (rows || []).reduce((total, row) => total + (Number(row?.[key]) || 0), 0);
    }

    /**
     * What one plan is called.
     *
     * `plan_detail` is the name the purchase wrote ("Starter — 200 credits"). The
     * `plan` relation the API also returns is NOT usable here: for a credit pack
     * order, plan_id holds the pack's id, and the relation resolves it against
     * oxzin_indiapicture_plan, so it comes back as an unrelated plan of the same id.
     */
    packageLabel(pkg: any): string {
        return (pkg?.plan_detail || '').trim() || 'Plan';
    }

    /**
     * A plan's credits left and granted. Exclusive plans keep credits in
     * credits_remaining / total_credits (their total_images / total_remaining read
     * 0); IndiaPicture plans keep them in total_remaining / total_images.
     */
    packageCreditsLeft(pkg: any): number {
        return Number(pkg?.credits_remaining ?? pkg?.total_remaining) || 0;
    }

    packageCreditsTotal(pkg: any): number {
        return Number(pkg?.total_credits ?? pkg?.total_images) || 0;
    }

    /** Credits spent on a plan, derived rather than read — no column holds it. */
    packageUsed(pkg: any): number {
        return Math.max(this.packageCreditsTotal(pkg) - this.packageCreditsLeft(pkg), 0);
    }

    /** Images downloaded on a plan's image allowance. */
    packageImagesUsed(pkg: any): number {
        const total = Number(pkg?.image_allowance) || 0;
        const left = Number(pkg?.images_remaining) || 0;
        return Math.max(total - left, 0);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    showLoginModal(event: Event): void {
        event.preventDefault();
        this.modalService.showLoginModal();
    }

    //  showPremiumLoginModal(event: Event): void {
    //     event.preventDefault();

    //     if(this.currentUser && this.currentUser?.isExclusive && this.currentUser?.isExclusive == true){
    //         // redirect to excluisve website
    //     }else{
    //         this.modalService.showPremiumLoginModal();
    //     }
        
    //     // this.modalService.showLoginModal();

    // }


    logout(){
         this.authService.logout();
         this.currentUser = null;
        this.router.navigate(['/']);
    }
}
